/**
 * Snippets and history, kept apart per scope.
 *
 * Two terminals with two different vocabularies: a project session takes Russian
 * prompts for Codex, the console takes shell commands. One shared list would mean
 * scrolling past «Проведи анализ кода» to reach `df -h`, and a shell history полный
 * of prose. The codex keys keep their original names so nothing already saved is lost.
 */
export type Scope = 'codex' | 'console';

const SNIPPETS_KEY: Record<Scope, string> = {
  codex: 'mac-remote:snippets',
  console: 'mac-remote:console-snippets',
};
const HISTORY_KEY: Record<Scope, string> = {
  codex: 'mac-remote:history',
  console: 'mac-remote:console-history',
};
const HISTORY_MAX = 60;

/** Typing on glass is the bottleneck, so the defaults are the things actually
 *  worth typing. Editable — these are only a starting point. */
export const DEFAULT_SNIPPETS = [
  'Проведи анализ кода',
  'Запусти тесты',
  'git status',
  'Что изменилось в последнем коммите?',
  'Закоммить изменения',
];

/**
 * The console's own starting set: what this Mac actually gets asked from a phone.
 *
 * `sudo -v` is here on purpose and is the most useful of the five — it does nothing but
 * refresh the sudo timestamp, so it turns "type a password on glass in the middle of a
 * command" into "unlock first, then work". The timestamp is per-tty and the console's
 * tty is stable, so everything for the next five minutes goes through without asking.
 */
export const DEFAULT_CONSOLE_SNIPPETS = [
  'sudo -v',
  'df -h /',
  'pmset -g',
  'tail -n 50 ~/Library/Logs/mac-remote-agent.log',
  'tmux -L mac-remote list-sessions',
];

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // A corrupt entry must not brick the app — fall back and move on.
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or private mode — not worth failing a send over */
  }
}

export function loadSnippets(scope: Scope): string[] {
  return read<string[]>(
    SNIPPETS_KEY[scope],
    scope === 'console' ? DEFAULT_CONSOLE_SNIPPETS : DEFAULT_SNIPPETS,
  );
}

export function saveSnippets(scope: Scope, list: string[]): void {
  write(SNIPPETS_KEY[scope], list);
}

export function loadHistory(scope: Scope): string[] {
  return read<string[]>(HISTORY_KEY[scope], []);
}

/**
 * Newest first, de-duplicated: re-sending the same prompt should move it up, not
 * fill the list with copies.
 *
 * Nothing typed as a password ever reaches here — the console sends those through a
 * path that touches neither this list nor the draft, because both live in the phone's
 * localStorage and a sudo password has no business being written to disk on a phone.
 */
export function pushHistory(scope: Scope, text: string): string[] {
  const t = text.trim();
  if (!t) return loadHistory(scope);
  const next = [t, ...loadHistory(scope).filter((h) => h !== t)].slice(0, HISTORY_MAX);
  write(HISTORY_KEY[scope], next);
  return next;
}

export function clearHistory(scope: Scope): void {
  write(HISTORY_KEY[scope], []);
}
